"use client"
import React, { Fragment, useState, useEffect } from "react";
import { Alert, Modal, Table } from "react-bootstrap";
import { useMediaQuery } from "react-responsive";
import { useAppData } from "@/lib/providers/AppDataProvider";
import ServiceLinkContent from "@/components/locator/ServiceLinkContent";

import "@/components/locator/ServiceProfileDetailsModal.css";

import { SERVICE_PROFILE_ACTION_OPTIONS } from "@/components/crm/serviceProfile/constants";
import { SERVICE_PROFILE_CONDITION_OPTIONS } from "@/components/crm/serviceProfile/constants";

const ServiceProfileDetailsModal = ({ profile, onClose }) => {
  const isSm = useMediaQuery({ query: "(max-width: 767.98px)" });

  const { flattenedServiceCategories: serviceCategoriesFlattened } = useAppData();

  const prettyPrintServiceListingCategories = (categories, items, tags) => {
    if (!categories || !Array.isArray(categories)) {
      return prettyPrintServiceListingItems(items);
    }

    return categories.map(item => {
      const category = serviceCategoriesFlattened.find(sc => sc.value === item);
      if (category) {
        let filteredItems = items.filter(item =>
          item.startsWith(category.value)
        );
        return (
          <>
            <div className="tags">
              <span className="tag service-profile category medium">
                {category.label}
              </span>
            </div>
            <>{prettyPrintServiceListingItems(filteredItems)}</>
            {tags && (
              <div className="tags">
                {tags?.map(tag => {
                  return (
                    <div>
                      <span className="tag medium">{tag}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        );
      }
    });
  };

  const prettyPrintServiceListingItems = items => {
    if (!items || !Array.isArray(items)) {
      return "";
    }
    return (
      <div>
        <span className="service-profile items medium">
          {items
            .map(
              item =>
                serviceCategoriesFlattened.find(sc => sc.value === item)?.label
            )
            .filter(name => name != null)
            .join(", ")}
        </span>
      </div>
    );
  };

  const prettyPrintServiceListingActions = (actions, conditions) => {
    let actionsLabels = actions.map(action => {
      const actionLabel = SERVICE_PROFILE_ACTION_OPTIONS.find(a => a.value === action)?.label;
      return (
        <div>
          <span className={`tag action ${action} medium`}>{actionLabel}</span>
        </div>
      );
    });
    let conditionsLabels = conditions.map(condition => {
      const conditionLabel = SERVICE_PROFILE_CONDITION_OPTIONS.find(c => c.value === condition)?.label;
      return (
        <div>
          <span className={`tag condition ${condition} medium`}>
            {conditionLabel}
          </span>
        </div>
      );
    });
    return (
      <div className="tags">
        {actionsLabels}
        {conditionsLabels}
      </div>
    );
  };

  return (
    <Modal
      show={true}
      onHide={onClose}
      size="md"
      fullscreen={isSm}
      centered
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title>{profile.displayName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {profile?.serviceProfile?.guidelines && (
          <Alert variant="info">
            <h6>Before You Visit</h6>
            <span className="medium">
              Although we do our best to stay up-to-date, information is
              constantly changing and we encourage you to call ahead to verify
              hours and availability.
            </span>
            <ServiceLinkContent content={profile?.serviceProfile?.guidelines} />
          </Alert>
        )}

        <Table className="service-profile-details-table">
          <tbody>
            {profile?.serviceProfile?.serviceListings?.map((sl, idx) => {
              return (
                <Fragment key={idx}>
                  <tr
                    className={
                      sl.notes
                        ? "service-listing-row with-notes"
                        : "service-listing-row"
                    }
                  >
                    <td>
                      {prettyPrintServiceListingActions(
                        sl.actions,
                        sl.conditions
                      )}
                    </td>
                    <td>
                      {prettyPrintServiceListingCategories(
                        sl.categories,
                        sl.items,
                        sl.tags
                      )}
                    </td>
                  </tr>
                  {(sl.feeRequired || sl.subscriptionRequired || sl.notes) && (
                    <tr className="service-listing-row">
                      <td colSpan={2}>
                        {sl.feeRequired && (
                          <div>
                            <span className="notes-text with-icon">
                              <i className="fa-solid fa-money-check-dollar"></i>
                              <span className="medium">
                                <strong>Fee Required</strong>
                              </span>
                            </span>
                          </div>
                        )}
                        {sl.subscriptionRequired && (
                          <div>
                            <span className="notes-text with-icon">
                              <i className="fa-kit fa-solid-clock-seven-circle-dollar"></i>
                              <span className="medium">
                                <strong>Subscription Required</strong>
                              </span>
                            </span>
                          </div>
                        )}
                        {sl.notes && (
                          <div>
                            <Alert variant="warning">
                              <i className="fa-solid fa-triangle-exclamation"></i>
                              <span
                                style={{ marginLeft: "0.25rem" }}
                                className="medium"
                              >
                                {sl.notes}
                              </span>
                            </Alert>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </Table>
      </Modal.Body>
    </Modal>
  );
};

export default ServiceProfileDetailsModal;
